import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  Button,
  Card, 
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IntegrityIcon from '@mui/icons-material/VerifiedUser';
import ExcellenceIcon from '@mui/icons-material/Grade';
import ReliabilityIcon from '@mui/icons-material/Security';
import Image from '../components/Image';

// Import images from centralized image config
import { ABOUT, BANNERS } from '../assets/images/imageConfig';

// Images - Using references from the image config file
const ABOUT_BANNER = BANNERS.ABOUT;
const COMPANY_HISTORY = ABOUT.COMPANY_HISTORY;
const MISSION_IMAGE = ABOUT.MISSION;
const VISION_IMAGE = ABOUT.VISION;

const About = () => {
  return (
    <Box>
      {/* Hero Banner */}
      <Box 
        sx={{
          position: 'relative',
          height: { xs: '200px', md: '300px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 86, 179, 0.85)',
              zIndex: 1
            }
          }}
        >
          <Image 
            src={ABOUT_BANNER}
            alt="About SafeNext"
            sx={{ width: '100%', height: '100%' }}
          />
        </Box>
        <Box sx={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' } 
            }}
          >
            About SafeNext
          </Typography>
          <Typography variant="h6">
            Know our story, mission, and vision
          </Typography>
        </Box>
      </Box>

      {/* Our Story */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            color="primary"
            sx={{ fontWeight: 700 }}
          >
            Our Story
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', mb: 2 }}>
            From humble beginnings to industry leaders
          </Typography>
          <Divider sx={{ width: 80, mx: 'auto', borderColor: 'primary.main', borderWidth: 2 }} />
        </Box>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
              <Image 
                src={COMPANY_HISTORY}
                alt="Company History"
                sx={{ height: 400, width: '100%' }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography paragraph>
              <strong>SafeNext was founded in 2025</strong> by <strong>Mr. Shikhar Singh</strong> with a vision to transform 
              the manpower service industry in India. What started as a small security agency with just 
              10 guards has now grown into a comprehensive manpower solutions provider with over 1,000 employees 
              serving clients across multiple sectors.
            </Typography>
            <Typography paragraph>
              Our journey began when our founder, Mr. Shikhar Singh, a former security officer, identified 
              significant gaps in the quality of security services available in the market. He believed that 
              security services should not just be about physical presence but should encompass a comprehensive 
              approach to safety and risk management.
            </Typography>
            <Typography paragraph>
              Over the years, we expanded our services to include housekeeping and skilled/unskilled labor 
              supply, maintaining our core values of integrity, professionalism, and excellence in every 
              aspect of our operations. Today, we are proud to be recognized as one of the most trusted names 
              in the manpower service industry, known for our reliability, quality of service, and commitment 
              to customer satisfaction.
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Mission and Vision */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.alternate' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  borderRadius: 4,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 70, 
                      height: 70,
                      mr: 2
                    }}
                  >
                    <FlagIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h4" component="h3" color="primary" sx={{ fontWeight: 600 }}>
                    Our Mission
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    mb: 3, 
                    height: 200, 
                    borderRadius: 2, 
                    overflow: 'hidden' 
                  }}
                >
                  <Image 
                    src={MISSION_IMAGE}
                    alt="Our Mission"
                  />
                </Box>
                
                <Typography paragraph>
                  To provide exceptional manpower services that meet and exceed our clients' expectations 
                  through continuous improvement, innovation, and a commitment to excellence. We strive to:
                </Typography>
                
                <List>
                  {[
                    'Deliver high-quality services that create value for our clients',
                    'Maintain the highest standards of professionalism and integrity',
                    'Provide a safe and supportive working environment for our employees',
                    'Contribute positively to the communities we serve'
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ p: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  borderRadius: 4,
                  display: 'flex',
                  flexDirection: 'column' 
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 70, 
                      height: 70,
                      mr: 2 
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h4" component="h3" color="primary" sx={{ fontWeight: 600 }}>
                    Our Vision
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    mb: 3, 
                    height: 200, 
                    borderRadius: 2, 
                    overflow: 'hidden' 
                  }}
                >
                  <Image 
                    src={VISION_IMAGE}
                    alt="Our Vision"
                  />
                </Box>
                
                <Typography paragraph>
                  To be the leading manpower service provider in India, recognized for our commitment to:
                </Typography>
                
                <List>
                  {[
                    'Excellence in service delivery',
                    'Innovation in manpower solutions',
                    'Ethical business practices',
                    'Employee welfare and development',
                    'Customer satisfaction and loyalty'
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ p: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
                
                <Typography sx={{ mt: 'auto' }}>
                  We envision a future where SafeNext is synonymous with quality, reliability, and integrity 
                  in the manpower service industry.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Core Values */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            color="primary"
            sx={{ fontWeight: 700 }}
          >
            Our Core Values
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', mb: 2 }}>
            The principles that guide our operations
          </Typography>
          <Divider sx={{ width: 80, mx: 'auto', borderColor: 'primary.main', borderWidth: 2 }} />
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 4, 
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 80, 
                      height: 80, 
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <IntegrityIcon sx={{ fontSize: 50 }} />
                  </Avatar>
                  <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Integrity
                  </Typography>
                </Box>
                <Typography align="center">
                  We conduct our business with the highest ethical standards, honesty, and transparency in all our dealings.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 4, 
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 80, 
                      height: 80, 
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <ExcellenceIcon sx={{ fontSize: 50 }} />
                  </Avatar>
                  <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Excellence
                  </Typography>
                </Box>
                <Typography align="center">
                  We strive for excellence in everything we do, constantly improving our services and processes.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                borderRadius: 4, 
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 80, 
                      height: 80, 
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <ReliabilityIcon sx={{ fontSize: 50 }} />
                  </Avatar>
                  <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Reliability
                  </Typography>
                </Box>
                <Typography align="center">
                  We are committed to being dependable partners for our clients, delivering on our promises consistently.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Additional CTA Section - Replacing the Join Our Team section */}
      <Box 
        sx={{
          bgcolor: 'primary.main',
          py: { xs: 6, md: 8 },
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Ready to Experience SafeNext Services?
              </Typography>
              <Typography variant="h6" gutterBottom>
                Discover how our high-quality manpower solutions can benefit your business
              </Typography>
              <Typography>
                Whether you need security personnel, housekeeping staff, or skilled labor for your operations, 
                our team is ready to provide tailored solutions that meet your specific requirements.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)'
                  }
                }}
              >
                Contact Us Today
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About; 